import {
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
  PositionStrategy,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { PlatformLocation } from '@angular/common';
import { ComponentRef, Injectable } from '@angular/core';

import moment from 'moment-timezone';
import { filter, first, interval, map, switchMap, take } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';

import { LocalStorageService } from '@recruitee/common';
import { WindowRef } from '@recruitee/user-interface';

import { ReloadNotificationComponent } from '../ui/reload-notification/reload-notification.component';

@Injectable({ providedIn: 'root' })
export class ReloadNotificationService {
  private readonly APP_REFRESH_CHECK_INTERVAL = 7 * 24 * 3600 * 1000;
  private readonly LAST_SEEN_IN_DAYS = 7;
  private readonly STORAGE_KEY = 'reload_notification_timestamp';

  constructor(
    private overlay: Overlay,
    private platformLocation: PlatformLocation,
    private localStorage: LocalStorageService,
    private windowRef: WindowRef,
  ) {
    this.bindTriggerHelper();
  }

  public init() {
    this.fetchIndexEtag()
      .pipe(
        first(),
        switchMap(initialEtag =>
          interval(this.APP_REFRESH_CHECK_INTERVAL).pipe(
            filter(() => this.shouldFetchEtag()),
            switchMap(() => this.fetchIndexEtag()),
            filter(currentEtag => initialEtag === currentEtag),
          ),
        ),
        take(1),
      )
      .subscribe(() => {
        this.setCacheTimestamp();
        this.triggerNotification();
      });
  }

  private triggerNotification(): void {
    const componentPortal = new ComponentPortal(ReloadNotificationComponent);
    const overlayRef = this.createOverlay();
    const componentRef = overlayRef.attach(componentPortal);
    componentRef.changeDetectorRef.detectChanges();

    this.handleComponentRefEvents(componentRef, overlayRef);
  }

  private handleComponentRefEvents(
    componentRef: ComponentRef<ReloadNotificationComponent>,
    overlayRef: OverlayRef,
  ): void {
    componentRef.instance.close$.pipe(take(1)).subscribe(() => {
      overlayRef.dispose();
    });
    componentRef.instance.reload$.pipe(take(1)).subscribe(() => {
      overlayRef.dispose();
      this.windowRef.nativeWindow.location.reload();
    });
  }

  private createOverlay(): OverlayRef {
    const globalPositionStrategy = this.getGlobalOverlayStrategy({ left: 85, bottom: 20 });
    return this.getOverlayRef(globalPositionStrategy);
  }

  private getGlobalOverlayStrategy(boundaries: {
    left: number;
    bottom: number;
  }): GlobalPositionStrategy {
    return this.overlay
      .position()
      .global()
      .left(`${boundaries.left}px`)
      .bottom(`${boundaries.bottom}px`);
  }

  private getOverlayRef(positionStrategy: PositionStrategy): OverlayRef {
    return this.overlay.create({ positionStrategy, panelClass: 'rt-overlay-panel-index' });
  }

  private shouldFetchEtag(): boolean {
    const cachedTimestamp = this.getCacheTimestamp();
    if (!cachedTimestamp) return true;

    return moment().diff(moment(cachedTimestamp), 'days') >= this.LAST_SEEN_IN_DAYS;
  }

  private fetchIndexEtag() {
    const url = `/${this.platformLocation.getBaseHrefFromDOM()}/index.html`.replace(/\/\/+/g, '/');
    return fromFetch(url, { method: 'HEAD' }).pipe(map(response => response.headers.get('etag')));
  }

  private setCacheTimestamp(): void {
    const timestamp = moment().format();
    this.localStorage.set(this.STORAGE_KEY, timestamp);
  }

  private getCacheTimestamp(): string | null {
    return this.localStorage.get(this.STORAGE_KEY, null);
  }

  private bindTriggerHelper(): void {
    // @ts-ignore
    this.windowRef.nativeWindow['reload_notification_trigger'] =
      this.triggerNotification.bind(this);
  }
}
